import {
  Compass,
  DraftingCompass,
  Layers,
  Orbit,
  type LucideIcon,
} from "lucide-react";
import { AboutReveal, AboutStagger, AboutStaggerItem } from "./about-reveal";
import { useSiteLanguage } from "@/lib/useSiteLanguage";
import { howWeWorkCopy } from "@/lib/aboutPageCopy";

const stepMeta: {
  icon: LucideIcon;
  step: string;
  accent: string;
  iconWrap: string;
  glow: string;
}[] = [
  {
    icon: Compass,
    step: "01",
    accent: "from-sky-500/15 via-sky-500/5 to-transparent",
    iconWrap:
      "bg-sky-500/15 text-sky-700 ring-sky-500/20 dark:bg-sky-400/15 dark:text-sky-300 dark:ring-sky-400/25",
    glow: "bg-sky-400/25",
  },
  {
    icon: DraftingCompass,
    step: "02",
    accent: "from-amber-500/15 via-amber-500/5 to-transparent",
    iconWrap:
      "bg-amber-500/15 text-amber-800 ring-amber-500/20 dark:bg-amber-400/15 dark:text-amber-300 dark:ring-amber-400/25",
    glow: "bg-amber-400/25",
  },
  {
    icon: Layers,
    step: "03",
    accent: "from-emerald-500/15 via-emerald-500/5 to-transparent",
    iconWrap:
      "bg-emerald-500/15 text-emerald-800 ring-emerald-500/20 dark:bg-emerald-400/15 dark:text-emerald-300 dark:ring-emerald-400/25",
    glow: "bg-emerald-400/25",
  },
  {
    icon: Orbit,
    step: "04",
    accent: "from-rose-500/12 via-rose-500/5 to-transparent",
    iconWrap:
      "bg-rose-500/15 text-rose-800 ring-rose-500/20 dark:bg-rose-400/15 dark:text-rose-300 dark:ring-rose-400/25",
    glow: "bg-rose-400/20",
  },
];

export function HowWeWork() {
  const lang = useSiteLanguage();
  const t = howWeWorkCopy[lang];

  return (
    <section
      id="how-we-work"
      className="scroll-mt-24 bg-background pb-20 md:pb-28 lg:pb-32"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <AboutReveal className="mx-auto mb-12 max-w-2xl text-center md:mb-16">
          <span className="mb-4 block text-[11px] font-medium uppercase tracking-[0.22em] text-muted-foreground">
            {t.eyebrow}
          </span>
          <h2 className="text-balance text-2xl font-bold tracking-tight text-foreground md:text-3xl lg:text-[2.15rem]">
            {t.title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground md:text-[15px]">
            {t.subtitle}
          </p>
        </AboutReveal>

        <AboutStagger className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4 lg:gap-5">
          {stepMeta.map((item, index) => {
            const Icon = item.icon;
            const copy = t.steps[index];
            return (
              <AboutStaggerItem key={item.step}>
                <article className="group relative flex h-full flex-col overflow-hidden rounded-[1.5rem] border border-border/80 bg-card p-6 shadow-[0_12px_40px_-28px_rgba(0,0,0,0.2)] transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_20px_50px_-24px_rgba(0,0,0,0.28)] md:p-7 dark:shadow-none">
                  <div
                    aria-hidden
                    className={`pointer-events-none absolute inset-0 bg-gradient-to-br ${item.accent}`}
                  />
                  <div
                    aria-hidden
                    className={`pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full ${item.glow} blur-3xl transition-opacity duration-500 opacity-60 group-hover:opacity-100`}
                  />

                  <div className="relative mb-6 flex items-center justify-between">
                    <span
                      className={`flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ${item.iconWrap} shadow-sm transition-transform duration-300 group-hover:scale-105`}
                    >
                      <Icon className="h-5 w-5" strokeWidth={1.6} />
                    </span>
                    <span className="text-[11px] font-semibold tracking-[0.2em] text-muted-foreground">
                      {item.step}
                    </span>
                  </div>

                  <h3 className="relative text-[15px] font-semibold tracking-tight text-foreground md:text-base">
                    {copy.title}
                  </h3>
                  <p className="relative mt-2.5 text-sm leading-relaxed text-muted-foreground">
                    {copy.description}
                  </p>
                </article>
              </AboutStaggerItem>
            );
          })}
        </AboutStagger>
      </div>
    </section>
  );
}
